import './space-theme.css';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import N8nOrbitalChat from './N8nOrbitalChat';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

// N8n Chat Component - using external file
// This is now imported from N8nOrbitalChat.js

const supabaseUrl = "https://hczdtxlujnnpighoudhj.supabase.co";
const supabaseKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhjemR0eGx1am5ucGlnaG91ZGhqIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0NjcyNzAyNCwiZXhwIjoyMDYyMzAzMDI0fQ.n8fXEH9nE94DK6MJFWBp6RWZr7EEnFYyNjrV0dgKh4A";
const supabase = createClient(supabaseUrl, supabaseKey);

// Configuration
const CONFIG = {
  APP_TITLE: "Orbital IQ Dashboard",
  COUNTRY_COLUMN: "country_of_operator_owner",
  ORBIT_CLASSES: ["LEO", "MEO", "GEO", "HEO"],
  PIE_COLORS: [
    "#FF4500", "#1E90FF", "#32CD32", "#FFD700", "#9370DB", 
    "#FF69B4", "#00CED1", "#FF7F50", "#6A5ACD", "#20B2AA"
  ]
};

// Visual Counter Animation Component
const VisualCounterAnimation = ({ finalValue }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    let animationFrameId;
    const duration = 2000; // Animation duration in ms
    const startTime = performance.now();
    const startValue = 0;
    const totalChange = finalValue || 0;
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-out cubic for very smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = Math.round(startValue + (totalChange * easeOutCubic));
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } 
    };
    
    // Start animation immediately
    animationFrameId = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [finalValue]);
  
  return displayValue.toLocaleString();
};

// Main App Component
function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Summary metrics (from Version 2's efficient approach)
  const [totalSatellites, setTotalSatellites] = useState(0);
  const [launchesSince2025, setLaunchesSince2025] = useState(0);
  const [purposeCount, setPurposeCount] = useState([]);
  
  // Full data for charts
  const [satelliteData, setSatelliteData] = useState([]);
  const [contractorCounts, setContractorCounts] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [selectedOrbitClass, setSelectedOrbitClass] = useState('LEO');
  const [usersData, setUsersData] = useState([]);

  // Get country code
  const getCountryCode = (name) => {
    const countryMap = {
      'USA': 'US',
      'United States': 'US',
      'UK': 'GB',
      'England': 'GB',
      'Britain': 'GB',
      'United Kingdom': 'GB',
      'Russia': 'RU',
      'Russian Federation': 'RU',
      'China': 'CN',
      "People's Republic of China": 'CN',
      'Japan': 'JP',
      'France': 'FR',
      'Germany': 'DE',
      'India': 'IN',
      'Canada': 'CA',
      'Italy': 'IT',
      'ESA': 'EU',
      'NASA': 'US',
      'International': 'UN',
      'Switzerland': 'CH',
    };

    if (!name || name === 'Unknown') return null;
    if (name.includes('/')) {
      const countries = name.split('/');
      for (const country of countries) {
        if (countryMap[country.trim()]) {
          return countryMap[country.trim()];
        }
      }
    }
    return countryMap[name] || null;
  };

  // Truncate name
  const truncateName = (name, maxLength = 40) => {
    if (!name) return 'Unknown';
    return name.length <= maxLength ? name : name.slice(0, maxLength - 3) + '...';
  };

  // Parse date - schema shows date_of_launch as DATE field
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      // Handle both string dates and Date objects from Supabase
      const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
      if (isNaN(date.getTime())) return null;
      return date;
    } catch {
      return null;
    }
  };

  // Process data
  const processData = useCallback((rawData) => {
    console.log(`=== PROCESSING ${rawData.length} TOTAL SATELLITE RECORDS ===`);
    
    // Dynamic threshold: warn if we loaded fewer than 90% of the server-reported total
    // This avoids hard-coding a count that breaks as the catalog grows
    if (rawData.length === 0) {
      console.error('❌ ERROR: No satellite records loaded — check database connectivity.');
    } else if (rawData._expectedTotal && rawData.length < rawData._expectedTotal * 0.9) {
      console.warn(`⚠️ WARNING: Loaded ${rawData.length} records but server reports ${rawData._expectedTotal}. Possible pagination failure.`);
    }
    
    const dataWithDates = rawData.map((d) => {
      const date = parseDate(d['date_of_launch'] || null); 
      return {
        ...d,
        'date_of_launch': date,
        'launch_year': date ? date.getFullYear() : null,
      };
    });

    // Process contractors with better field detection and normalization
    const contractorCounts = {};
    let spacexCount = 0;
    let planetLabsCount = 0;
    
    dataWithDates.forEach(d => {
      // Very defensive contractor detection — try all possible variations
      let contractor = d['contractor'] || d['operator_owner'] || d['Operator_Owner'] || d['Operator/Owner'] || d['CONTRACTOR'] || 'Unknown';
      
      // Fallback: if we still have nothing but the object has records, try to find any field containing 'operator' or 'contractor'
      if (contractor === 'Unknown') {
        const possibleKeys = Object.keys(d);
        const autoDetectedKey = possibleKeys.find(k => 
          k.toLowerCase().includes('contractor') || 
          k.toLowerCase().includes('operator') || 
          k.toLowerCase().includes('owner')
        );
        if (autoDetectedKey) contractor = d[autoDetectedKey];
      }
      
      // Better normalization for SpaceX and variations
      const lowerContractor = String(contractor).toLowerCase();
      
      if (lowerContractor.includes('spacex') || lowerContractor.includes('space x')) {
        contractor = 'SpaceX';
        spacexCount++;
      } else if (lowerContractor.includes('starlink')) {
        contractor = 'SpaceX';
        spacexCount++;
      } else if (lowerContractor.includes('planet') && lowerContractor.includes('lab')) {
        contractor = 'Planet Labs';
        planetLabsCount++;
      } else if (lowerContractor.includes('boeing')) {
        contractor = 'Boeing';
      } else if (lowerContractor.includes('lockheed')) {
        contractor = 'Lockheed Martin';
      } else if (lowerContractor.includes('casc')) {
        contractor = 'CASC (China Aerospace Science and Technology Corporation)';
      } else if (lowerContractor.includes('cast')) {
        contractor = 'CAST (China Academy of Space Technology)';
      }
      
      // Count the contractor
      contractorCounts[contractor] = (contractorCounts[contractor] || 0) + 1;
    });

    // Convert to array and sort
    const contractors = Object.entries(contractorCounts).map(([contractor, count]) => {
      const country = 'Unknown'; // We'd need to map contractors to countries
      return {
        contractor,
        count,
        country,
        'Country Code': getCountryCode(country),
        'Contractor Truncated': truncateName(contractor),
      };
    }).sort((a, b) => b.count - a.count);

    console.log('=== FINAL CONTRACTOR ANALYSIS ===');
    console.log('Top 10 contractors by count:', contractors.slice(0, 10));
    console.log('SpaceX individual count tracking:', spacexCount);
    console.log('SpaceX final count:', contractorCounts['SpaceX'] || 0);
    console.log('Planet Labs individual count tracking:', planetLabsCount);
    console.log('Planet Labs final count:', contractorCounts['Planet Labs'] || 0);
    
    if (contractorCounts['SpaceX'] < 1000) {
      console.error('❌ SpaceX count is suspiciously low:', contractorCounts['SpaceX']);
      console.error('Expected SpaceX to have 1000+ satellites');
    } else {
      console.log('✅ SpaceX count looks correct:', contractorCounts['SpaceX']);
    }
    console.log('==================================');
    
    setContractorCounts(contractors);

    // Process purpose counts from the FULL dataset
    console.log('=== PROCESSING PURPOSES FROM FULL DATASET ===');
    const purposeCounts = {};
    
    dataWithDates.forEach(d => {
      let p = d['purpose'] || 'Unknown';
      
      // Only do minimal normalization to group similar entries
      const lowerP = p.toLowerCase().trim();
      
      // Group communication variations but keep specific subcategories visible
      if (lowerP === 'communications') {
        p = 'Communications';
      } else if (lowerP.startsWith('communications/')) {
        // Keep specific communication subcategories separate
        p = p.trim();
      } else if (lowerP === 'earth observation' || lowerP === 'earth observation ') {
        p = 'Earth Observation';
      } else if (lowerP.startsWith('earth observation/')) {
        // Keep specific Earth Observation subcategories separate for now
        p = p.trim();
      } else if (lowerP === 'navigation') {
        p = 'Navigation';
      } else if (lowerP.startsWith('navigation/')) {
        p = p.trim();
      } else if (lowerP.startsWith('technology demonstration')) {
        p = p.trim();
      } else {
        // Keep all other purposes as-is, just trim whitespace
        p = p.trim();
      }
      
      purposeCounts[p] = (purposeCounts[p] || 0) + 1;
    });
    
    const purposeCountsArray = Object.entries(purposeCounts)
      .map(([purpose, count]) => ({ purpose, count }))
      .sort((a, b) => b.count - a.count);
    
    console.log('=== FULL DATASET PURPOSE DISTRIBUTION ===');
    console.log('Top 15 purposes from all', dataWithDates.length, 'records:');
    purposeCountsArray.slice(0, 15).forEach(({purpose, count}, index) => {
      console.log(`${index + 1}. ${purpose}: ${count.toLocaleString()}`);
    });
    
    // Check if Communications is actually #1
    const commsTotal = purposeCountsArray.filter(p => 
      p.purpose.toLowerCase().includes('comm')
    ).reduce((sum, p) => sum + p.count, 0);
    
    const eoTotal = purposeCountsArray.filter(p => 
      p.purpose.toLowerCase().includes('earth') && p.purpose.toLowerCase().includes('observ')
    ).reduce((sum, p) => sum + p.count, 0);
    
    console.log('Total Communications (all variants):', commsTotal.toLocaleString());
    console.log('Total Earth Observation (all variants):', eoTotal.toLocaleString());
    console.log('==========================================');
    
    setPurposeCount(purposeCountsArray);

    // Calculate users data with better field detection
    const usersCounts = dataWithDates.reduce((acc, d) => {
      const user = d['users'] || 'Unknown';
      acc[user] = (acc[user] || 0) + 1;
      return acc;
    }, {});

    const usersArray = Object.entries(usersCounts).map(([user, count]) => ({ user, count }));
    console.log('Top 5 users by count:', usersArray.sort((a, b) => b.count - a.count).slice(0, 5));
    setUsersData(usersArray.sort((a, b) => b.count - a.count));

    setSatelliteData(dataWithDates);
    setLoading(false);
  }, []);

  // Load data using Version 2's efficient approach
  useEffect(() => {
    const loadData = async () => {
      const TABLE = "Orbital IQ"; // Correct table name without extra quotes
      
      try {
        setLoading(true);
        setError(null);

        const today = new Date().toISOString().slice(0, 10);

        // 1) total satellites
        const { count: tot, error: totErr } = await supabase
          .from(TABLE)
          .select('*', { head: true, count: 'exact' });
        if (totErr) throw totErr;
        setTotalSatellites(tot);

        // 2) launches since 2025-01-01
        const { count: lch, error: lchErr } = await supabase
          .from(TABLE)
          .select('*', { head: true, count: 'exact' })
          .gte('date_of_launch', '2025-01-01')
          .lte('date_of_launch', today);
        if (lchErr) throw lchErr;
        setLaunchesSince2025(lch);

        // 3) DEBUG: Get first 10 records to examine field names and values
        const { data: sampleData, error: sampleErr } = await supabase
          .from(TABLE)
          .select('*')
          .limit(10);
        if (sampleErr) throw sampleErr;
        
        console.log('=== DEBUGGING SAMPLE DATA ===');
        const fieldNames = Object.keys(sampleData[0] || {});
        console.log('DETECTED FIELD NAMES:', fieldNames.join(', '));
        console.log('Sample data record 0:', sampleData[0]);
        console.log('Sample contractors (via operator_owner):', sampleData.map(d => d.operator_owner).filter(Boolean));
        console.log('Sample purposes:', sampleData.map(d => d.purpose).filter(Boolean));
        console.log('===============================');

        // 4) Get ALL contractor data for analysis - including nulls and handle inconsistencies
        // We select operator_owner but we'll try to detect the correct column if it fails
        const { data: contractorData, error: contractorErr } = await supabase
          .from(TABLE)
          .select('*') // Changed to * to be 100% safe against naming issues
          .limit(15000); 
        
        if (contractorErr) throw contractorErr;
        
        // Debug contractor field detection
        const contractorFieldStats = {
          'operator_owner (exists in keys)': contractorData[0] && 'operator_owner' in contractorData[0],
          'contractor (exists in keys)': contractorData[0] && 'contractor' in contractorData[0],
          'total_records': contractorData.length
        };
        console.log('Contractor field usage:', contractorFieldStats);
        
        // Get unique contractor values for analysis with defensive property access
        const uniqueContractors = [...new Set(
          contractorData.map(d => d.contractor || d.operator_owner).filter(Boolean)
        )].sort();
        console.log('Total unique contractors:', uniqueContractors.length);
        console.log('Top 20 unique contractors:', uniqueContractors.slice(0, 20));
        
        // Also check operator_owner field as backup
        const uniqueOperators = [...new Set(
          contractorData.map(d => d.operator_owner).filter(Boolean)
        )].sort();
        console.log('Top 20 unique operator_owners:', uniqueOperators.slice(0, 20));
        
        console.log('SpaceX variations in contractor field:', uniqueContractors.filter(c => 
          c.toLowerCase().includes('spacex') || c.toLowerCase().includes('space x') || c.toLowerCase().includes('starlink')
        ));
        console.log('SpaceX variations in operator_owner field:', uniqueOperators.filter(c => 
          c.toLowerCase().includes('spacex') || c.toLowerCase().includes('space x') || c.toLowerCase().includes('starlink')
        ));

        // 5) Get purpose data from the full paginated dataset instead of separate query
        console.log('Analyzing purposes from full dataset...');
        
        // We'll process purposes from the full dataset in processData()
        // Remove the separate purpose query since we have all data now

        // 7) load ALL data for charts - using pagination to get all records
        console.log('Loading ALL satellite data using pagination...');
        
        let allData = [];
        let from = 0;
        const batchSize = 1000; // Process in batches of 1000
        let hasMore = true;
        
        while (hasMore) {
          console.log(`Loading batch ${Math.floor(from/batchSize) + 1}: records ${from} to ${from + batchSize - 1}`);
          
          const { data: batchData, error: batchErr } = await supabase
            .from(TABLE)
            .select('*')
            .range(from, from + batchSize - 1);
          
          if (batchErr) throw batchErr;
          
          if (batchData && batchData.length > 0) {
            allData = allData.concat(batchData);
            from += batchSize;
            
            // If we got less than batchSize, we've reached the end
            if (batchData.length < batchSize) {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
          
          // Safety break to prevent infinite loops
          if (from > 20000) {
            console.warn('Safety break at 20,000 records');
            break;
          }
        }
        
        console.log(`Successfully loaded ${allData.length} total satellite records using pagination`);
        console.log(`Server-reported total: ${tot} | Loaded: ${allData.length}`);

        // Dynamic validation — warns if loaded data falls below 90% of the server count
        // so this adapts automatically as the catalog grows without needing code changes
        if (tot > 0 && allData.length < tot * 0.9) {
          console.warn(`⚠️ WARNING: Only loaded ${allData.length} of ${tot} server-reported records. Pagination may have stopped early.`);
        } else {
          console.log(`✅ Full dataset loaded successfully (${allData.length} records, ${((allData.length/tot)*100).toFixed(1)}% of server total).`);
        }

        // Tag data with server total for downstream validation in processData
        allData._expectedTotal = tot;
        
        processData(allData);

      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [processData]);

  if (loading) {
    return (
      <div className="loading">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          padding: '24px',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)'
        }}>
          <span style={{ 
            color: 'var(--cosmic-blue)', 
            fontSize: '24px', 
            fontWeight: '600' 
          }}>Loading Satellite Data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <div className="dashboard-card" style={{ marginTop: 'var(--spacing-lg)' }}>
          <h1 style={{ 
            color: 'var(--plasma-red)', 
            fontSize: '20px', 
            fontWeight: '600', 
            marginBottom: '8px' 
          }}>
            Error Loading Dashboard
          </h1>
          <p style={{ 
            color: 'var(--plasma-red)', 
            fontSize: '14px', 
            marginBottom: '16px' 
          }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Animated Background */}
      <div className="background-animation">
        <div className="stars"></div>
        <div className="nebula-glow"></div>
      </div>

      {/* Dashboard Header */}
      <div className="dashboard-header">
        <h1>{CONFIG.APP_TITLE}</h1>
        <p className="subtitle">Space Exploration Dashboard</p>
      </div>

      {/* Main Content */}
      <div style={{ padding: 'var(--spacing-lg)', maxWidth: '1600px', margin: '0 auto' }}>
        {/* Enhanced Summary Cards Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 'var(--spacing-lg)', marginBottom: 'var(--spacing-xl)' }}>
          {/* Total Satellites Card */}
          <div className="metric-card" style={{ 
            padding: '48px 40px',
            borderRadius: '16px',
            background: 'rgba(60, 65, 75, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div className="metric-value" style={{ 
              fontSize: '72px',
              fontWeight: 'bold',
              color: 'var(--cosmic-blue)',
              letterSpacing: '-0.5px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              minWidth: '300px',
              textAlign: 'center'
            }}>
              <VisualCounterAnimation finalValue={totalSatellites} />
            </div>
            <div className="metric-label" style={{ 
              fontSize: '24px', 
              fontWeight: '600',
              marginTop: '12px',
              color: 'var(--light-gray)'
            }}>
              Total Satellites
            </div>
            <div style={{ marginTop: '16px', color: 'var(--aurora-green)', fontSize: '18px', fontWeight: '500' }}>
              Live Database Count
            </div>
          </div>
          
          {/* Satellites This Year Card */}
          <div className="metric-card" style={{ 
            padding: '48px 40px',
            borderRadius: '16px',
            background: 'rgba(20, 32, 57, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div className="metric-value" style={{ 
              fontSize: '72px',
              fontWeight: 'bold',
              color: 'var(--stellar-cyan)',
              letterSpacing: '-0.5px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              <VisualCounterAnimation finalValue={launchesSince2025} />
            </div>
            <div className="metric-label" style={{ 
              fontSize: '20px', 
              fontWeight: '600',
              marginTop: '12px',
              color: 'var(--light-gray)',
              lineHeight: '1.4'
            }}>
              Satellites Launched
            </div>
            <div className="metric-label" style={{ 
              fontSize: '20px', 
              fontWeight: '600',
              marginTop: '4px',
              color: 'var(--light-gray)',
              lineHeight: '1.4'
            }}>
              This Year
            </div>
            <div style={{ marginTop: '16px', color: 'var(--stellar-cyan)', fontSize: '18px', fontWeight: '500' }}>
              As of {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
          
          {/* Most Common Orbit Card */}
          <div className="metric-card" style={{ 
            padding: '48px 40px',
            borderRadius: '16px',
            background: 'rgba(20, 32, 57, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div className="metric-value" style={{ 
              fontSize: '72px',
              fontWeight: 'bold',
              color: 'var(--aurora-green)',
              letterSpacing: '-0.5px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              {selectedOrbitClass}
            </div>
            <div className="metric-label" style={{ 
              fontSize: '24px', 
              fontWeight: '600',
              marginTop: '12px',
              color: 'var(--light-gray)'
            }}>
              Most Common Orbit
            </div>
            <div style={{ marginTop: '16px', color: 'var(--aurora-green)', fontSize: '18px', fontWeight: '500' }}>
              {satelliteData.filter(d => 
                (d['Class_of_Orbit'] === selectedOrbitClass) || 
                (d['class_of_orbit'] === selectedOrbitClass)
              ).length.toLocaleString()} satellites
            </div>
          </div>
        </div>

        {/* Main Charts Grid */}
        <div style={{ display: 'grid', gap: 'var(--spacing-lg)' }}>
         
          {/* Satellite Timeline - Full Width */}
          <div className="dashboard-card" style={{ width: '100%' }}>
            <h2>Satellite Deployment Timeline by Top 10 Countries</h2>
            <div className="chart-container" style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={(() => {
                    // Get launches by country and year with improved date parsing
                    const countriesData = satelliteData.reduce((acc, d) => {
                      let year = null;
                      
                      // Parse the Date of Launch
                      if (d['date_of_launch']) {
                        const dateStr = String(d['date_of_launch']).trim();
                        const parsedDate = new Date(dateStr);
                        
                        if (!isNaN(parsedDate.getTime())) {
                          year = parsedDate.getFullYear();
                        } else {
                          // Try parsing year from string directly
                          const match = dateStr.match(/(\d{4})/);
                          if (match) {
                            year = parseInt(match[1]);
                          }
                        }
                      }
                      
                      if (year && !isNaN(year) && year >= 1950 && year <= 2030) {
                        // Use Country of Operator/Owner column
                        const country = d['country_of_operator_owner'] || 'Unknown';
                        
                        if (!acc[year]) acc[year] = {};
                        if (!acc[year][country]) acc[year][country] = 0;
                        acc[year][country] += 1;
                      }
                      
                      return acc;
                    }, {});

                    // Get total launches by country to find top 10
                    const countryTotals = satelliteData.reduce((acc, d) => {
                      const country = d['country_of_operator_owner'] || 'Unknown';
                      acc[country] = (acc[country] || 0) + 1;
                      return acc;
                    }, {});

                    // Get top 10 countries
                    const top10Countries = Object.entries(countryTotals)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 10)
                      .map(([country]) => country);

                    // Transform data for LineChart
                    const years = Object.keys(countriesData).sort((a, b) => a - b);
                    const timelineData = years.map(year => {
                      const yearData = { year: parseInt(year) };
                      top10Countries.forEach(country => {
                        yearData[country] = countriesData[year][country] || 0;
                      });
                      return yearData;
                    });

                    return timelineData;
                  })()}
                  margin={{ top: 20, right: 30, left: 60, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis 
                    dataKey="year" 
                    stroke="var(--light-gray)"
                    tick={{ fill: 'var(--light-gray)', fontSize: 12 }}
                    label={{ value: 'Year', offset: -30, position: 'insideBottom' }}
                  />
                  <YAxis 
                    stroke="var(--light-gray)"
                    tick={{ fill: 'var(--light-gray)', fontSize: 12 }}
                    label={{ value: 'Number of Launches', angle: -90, position: 'insideLeft', offset: -40 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                    itemStyle={{ 
                      color: 'var(--white-star)',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    labelStyle={{ 
                      color: 'var(--cosmic-blue)',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      marginBottom: '6px'
                    }}
                  />
                  <Legend verticalAlign="top" height={36}/>
                  {(() => {
                    // Create top 10 countries array and colors
                    const countryTotals = satelliteData.reduce((acc, d) => {
                      const country = d['country_of_operator_owner'] || 'Unknown';
                      acc[country] = (acc[country] || 0) + 1;
                      return acc;
                    }, {});

                    const top10Countries = Object.entries(countryTotals)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 10)
                      .map(([country]) => country);

                    return top10Countries.map((country, index) => (
                      <Line
                        key={country}
                        type="monotone"
                        dataKey={country}
                        stroke={CONFIG.PIE_COLORS[index % CONFIG.PIE_COLORS.length]}
                        strokeWidth={2}
                        name={country}
                        dot={{ r: 3 }}
                        activeDot={{ r: 6 }}
                      />
                    ));
                  })()}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Two Column Layout */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))', gap: 'var(--spacing-lg)' }}>
            {/* Orbit Analysis */}
            <div className="dashboard-card">
              <h2>Orbit Analysis</h2>
              <div style={{ marginBottom: '24px' }}>
                <select
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: 'var(--white-star)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    width: '200px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  value={selectedOrbitClass}
                  onChange={(e) => setSelectedOrbitClass(e.target.value)}
                >
                  {CONFIG && CONFIG.ORBIT_CLASSES && CONFIG.ORBIT_CLASSES.map(orbit => (
                    <option key={orbit} value={orbit}>{orbit}</option>
                  ))}
                </select>
              </div>

              <div className="chart-container" style={{ height: '400px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart 
                    key={selectedOrbitClass}
                    margin={{ top: 20, right: 20, bottom: 40, left: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis 
                      dataKey="perigee_km" 
                      name="perigee"
                      unit=" km" 
                      stroke="var(--light-gray)"
                      tick={{ fill: 'var(--light-gray)', fontSize: 12 }}
                      type="number"
                      domain={selectedOrbitClass === 'LEO' ? [0, 1000] : [0, 'dataMax']}
                      allowDecimals={false}
                    />
                    <YAxis 
                      dataKey="apogee_km" 
                      name="apogee"
                      unit=" km"
                      stroke="var(--light-gray)"
                      tick={{ fill: 'var(--light-gray)', fontSize: 12 }}
                      type="number"
                      domain={selectedOrbitClass === 'LEO' ? [0, 1000] : [0, 'dataMax']}
                      allowDecimals={false}
                    />
                    <Tooltip 
                      cursor={{ strokeDasharray: '3 3' }}
                      contentStyle={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px'
                      }}
                      itemStyle={{ 
                        color: 'var(--white-star)',
                        fontSize: '14px'
                      }}
                      labelStyle={{ 
                        color: 'var(--cosmic-blue)',
                        fontWeight: 'bold'
                      }}
                    />
                    <Scatter 
                      name={selectedOrbitClass + " Orbits"} 
                      data={(() => {
                        // Filter and properly clean data - accounting for TEXT fields
                        const filteredData = satelliteData.filter(d => {
                          // Check both possible field names for orbit class
                          const hasOrbitClass = d['class_of_orbit'] === selectedOrbitClass;
                           
                          // Get perigee and apogee - these are TEXT fields in schema
                          let perigee = d['perigee_km'];
                          let apogee = d['apogee_km'];
                          
                          // Convert TEXT to numbers and clean
                          if (typeof perigee === 'string') {
                            perigee = parseFloat(perigee.replace(/[^\d.-]/g, '')) || 0;
                          } else {
                            perigee = Number(perigee) || 0;
                          }
                          
                          if (typeof apogee === 'string') {
                            apogee = parseFloat(apogee.replace(/[^\d.-]/g, '')) || 0;
                          } else {
                            apogee = Number(apogee) || 0;
                          }
                          
                          // Check if values are valid numbers
                          const isValidPerigee = !isNaN(perigee) && perigee >= 0;
                          const isValidApogee = !isNaN(apogee) && apogee >= 0;
                          
                          // For LEO, filter out anything above 1000km
                          if (selectedOrbitClass === 'LEO') {
                            return hasOrbitClass && isValidPerigee && isValidApogee && 
                                  perigee <= 1000 && apogee <= 1000;
                          }
                          
                          return hasOrbitClass && isValidPerigee && isValidApogee;
                        });
                        
                        // Transform data to ensure clean numeric values
                        const cleanData = filteredData.map(d => {
                          let perigee = d['perigee_km'];
                          let apogee = d['apogee_km'];
                          
                          // Clean and convert TEXT to numbers
                          if (typeof perigee === 'string') {
                            perigee = parseFloat(perigee.replace(/[^\d.-]/g, '')) || 0;
                          } else {
                            perigee = Number(perigee) || 0;
                          }
                          
                          if (typeof apogee === 'string') {
                            apogee = parseFloat(apogee.replace(/[^\d.-]/g, '')) || 0;
                          } else {
                            apogee = Number(apogee) || 0;
                          }
                          
                          return {
                            ...d,
                            'perigee_km': perigee,
                            'apogee_km': apogee,
                            'name_of_satellite_alternate_names': d['name_of_satellite'] || 'Unknown'
                          };
                        });
                        
                        return cleanData;
                      })()}
                      fill="var(--aurora-green)"
                      opacity={0.8}
                      r={5}
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Satellite Purpose */}
            <div className="dashboard-card">
              <h2>Satellite Purpose Distribution</h2>
              <div className="chart-container" style={{ height: '400px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={purposeCount.slice(0, 10)} 
                      dataKey="count"
                      nameKey="purpose"
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={130}
                      paddingAngle={2}
                      label={({ name, count, value }) => {
                        const total = purposeCount.reduce((sum, item) => sum + item.count, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        // Only show percentage if > 2% to avoid clutter
                        return percentage > 2 ? `${percentage}%` : '';
                      }}
                      labelLine={false}
                    >
                      {purposeCount.slice(0, 10).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={CONFIG.PIE_COLORS[index % CONFIG.PIE_COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <text 
                      x="50%" 
                      y="48%" 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      style={{ 
                        fill: 'var(--white-star)', 
                        fontSize: '24px', 
                        fontWeight: 'bold' 
                      }}
                    >
                      {totalSatellites.toLocaleString()}
                    </text>
                    <text 
                      x="50%" 
                      y="54%" 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      style={{ 
                        fill: 'var(--smoke-gray)', 
                        fontSize: '12px' 
                      }}
                    >
                      Total Satellites
                    </text>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        maxWidth: '200px'
                      }}
                      itemStyle={{ 
                        color: 'var(--white-star)',
                        fontSize: '12px'
                      }}
                      labelStyle={{ 
                        color: 'var(--white-star)',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                      formatter={(value, name) => [
                        `${value.toLocaleString()} satellites`,
                        name.length > 25 ? name.substring(0, 25) + '...' : name
                      ]}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={60}
                      layout="horizontal"
                      wrapperStyle={{ 
                        bottom: 0,
                        fontSize: '11px',
                        lineHeight: '1.2'
                      }}
                      formatter={(value) => 
                        value.length > 20 ? value.substring(0, 20) + '...' : value
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Purpose breakdown list */}
              <div style={{ 
                marginTop: '20px', 
                padding: '16px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                <h4 style={{ 
                  color: 'var(--white-star)', 
                  marginBottom: '12px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Complete Purpose Breakdown:
                </h4>
                {purposeCount.slice(0, 15).map(({ purpose, count }, index) => (
                  <div key={purpose} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 0',
                    borderBottom: index < 14 ? '1px solid rgba(255,255,255,0.1)' : 'none'
                  }}>
                    <span style={{ 
                      color: 'var(--light-gray)',
                      fontSize: '12px',
                      flex: 1
                    }}>
                      {index + 1}. {purpose}
                    </span>
                    <span style={{ 
                      color: 'var(--cosmic-blue)',
                      fontSize: '12px',
                      fontWeight: '600',
                      marginLeft: '8px'
                    }}>
                      {count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contractor Analysis - Full Width */}
          <div className="dashboard-card" style={{ width: '100%' }}>
            <h2>Contractor Analysis</h2>
            <div style={{ marginBottom: '24px' }}>
              <select 
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                  color: 'var(--white-star)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  width: '300px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                value={selectedContractors}
                onChange={(e) => setSelectedContractors(Array.from(e.target.selectedOptions, option => option.value))}
                multiple
              >
                {contractorCounts.map(c => (
                  <option key={c.contractor} value={c.contractor}>{c.contractor}</option>
                ))}
              </select>
            </div>

            <div className="chart-container" style={{ height: '500px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={contractorCounts
                    .filter(c => selectedContractors.length === 0 || selectedContractors.includes(c.contractor))
                    .slice(0, 15)}
                  layout="vertical"
                  margin={{ left: 200, right: 30, top: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis 
                    type="number" 
                    stroke="var(--light-gray)"
                    tick={{ fill: 'var(--light-gray)', fontSize: 12 }}
                  />
                  <YAxis 
                    dataKey="Contractor Truncated" 
                    type="category"
                    stroke="var(--light-gray)"
                    tick={{ fill: 'var(--light-gray)', fontSize: 12 }}
                    width={180}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar 
                    dataKey="count" 
                    fill="var(--stellar-cyan)"
                    name="Number of Satellites"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Users Data - Full Width */}
          <div className="dashboard-card" style={{ width: '100%' }}>
            <h2>Users Data</h2>
            <div className="chart-container" style={{ height: '600px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={usersData.sort((a, b) => b.count - a.count).slice(0, 15)}
                  layout="vertical"
                  margin={{ left: 150, right: 30, top: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis 
                    type="number" 
                    stroke="var(--light-gray)"
                    tick={{ fill: 'var(--light-gray)', fontSize: 12 }}
                  />
                  <YAxis 
                    dataKey="user" 
                    type="category"
                    stroke="var(--light-gray)"
                    tick={{ fill: 'var(--light-gray)', fontSize: 12 }}
                    width={140}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="var(--aurora-green)"
                    name="Satellites"
                    radius={[0, 6, 6, 0]}
                  >
                    {usersData.sort((a, b) => b.count - a.count).slice(0, 15).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={CONFIG.PIE_COLORS[index % CONFIG.PIE_COLORS.length]} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Add Real AI Assistant */}
      <N8nOrbitalChat />
    
      <footer style={{ 
        backgroundColor: 'var(--dark-navy)', 
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '20px 24px',
        textAlign: 'center',
        marginTop: '40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        {/* Left side - Data info */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <p style={{ color: 'var(--smoke-gray)', fontSize: '14px', margin: '0' }}>
            Live data: {totalSatellites.toLocaleString()} satellites loaded
            {totalSatellites < 12000 && (
              <span style={{ color: 'var(--plasma-red)', marginLeft: '10px' }}>
                ⚠️ Expected 12,500+ records
              </span>
            )}
          </p>
          <p style={{ color: 'var(--smoke-gray)', fontSize: '14px', margin: '4px 0 0 0' }}>
            Data updated: {new Date().toLocaleString()}
          </p>
        </div>

        {/* Center - Logo */}
        {/* DST Logo - Commented out */}
        {/* <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flex: '0 0 auto'
        }}>
          <img 
            src="/DST.png" 
            alt="Company Logo" 
            style={{ 
              height: '60px',
              width: 'auto',
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
          />
        </div> */}

        {/* Right side - Powered by - Commented out */}
        {/* <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end',
          fontSize: '12px',
          color: 'var(--smoke-gray)'
        }}>
          <div style={{ marginBottom: '4px' }}>
            Powered by Divergent Space Technologies
          </div>
          <div>
            Copyright 2025
          </div>
        </div> */}
      </footer>
      
    </div>
  );
}

export default App;